export type SessionDocumentationMode = 'required' | 'not_required';

export interface BuildApiSchemaMarkdownParams {
  agentName: string;
  flowId: string;
  summary: string;
  sessionMode: SessionDocumentationMode;
  inputCount: number;
  apiBaseUrl: string;
  exampleUrlWithSession: string;
  tableRows: Array<{
    index: number;
    inputId: string;
    purpose: string;
  }>;
  fullExampleBodyJson: string;
}

function escapeTableCell(value: string): string {
  return value.replace(/\|/g, '/');
}

const STATIC_OVERVIEW_THROUGH_REQUEST = `## Overview
This is a connection to the FluxPrompt API system, for custom-built Ai Agents.

## Authentication
All requests use a single API key passed as a **request header** — not a query param, not a Bearer token.
\`\`\`
Header name:  api-key
Header value: <your key>
\`\`\`
`;

const STATIC_REQUEST_FORMAT = `## Request Format

Every request is a \`POST\` with \`Content-Type: application/json\`. The body always has this shape:

\`\`\`json
{
  "variableInputs": [
    { "inputId": "varInputNode_XXXXXXXXXXXXXXX_X.XXXX", "inputText": "..." },
    { "inputId": "varInputNode_XXXXXXXXXXXXXXX_X.XXXX", "inputText": "..." }
  ]
}
\`\`\`

**Critical rules:**
- \`variableInputs\` is an ordered array — order matters for some flows.
- Every \`inputId\` must match exactly what the flow expects. Wrong IDs produce no error — the input is silently ignored and the flow returns a canned/default response.
- All values are strings. Even numeric parameters (temperature, max_tokens) are serialized as a JSON-fragment string (see Model Parameters section).
- Missing inputs do not throw an HTTP error. They produce incorrect AI behavior or a guardrail response. Always send all expected inputs.

---

`;

const STATIC_RESPONSE_PARSING = `## Response Parsing

### Actual Response Structure

The API returns JSON with this exact structure:

\`\`\`json
{
  "status": "success",
  "data": {
    "message": [
      {
        "timestamp": "3/14/2026, 1:48:21 AM",
        "text": "Here is the answer to your question...",
        "outputId": "outputObjectNode_1773364137836_0.6070"
      }
    ]
  }
}
\`\`\`

**Extraction path:** \`response.data.data.message[0].text\`

### Robust Parser

Do not hardcode a single path — the structure can vary between flows. Use a defensive parser:

\`\`\`ts
function extractResponseText(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const d = data as Record<string, unknown>;

  // Primary path — actual FluxPrompt structure
  if (d.data && typeof d.data === "object") {
    const inner = d.data as Record<string, unknown>;
    if (Array.isArray(inner.message) && inner.message.length > 0) {
      const first = inner.message[0] as Record<string, unknown>;
      if (typeof first.text === "string" && first.text) return first.text;
    }
  }

  // Fallback: flat string fields
  for (const key of ["output", "result", "message", "text", "response", "content", "answer"]) {
    if (typeof d[key] === "string" && d[key]) return d[key] as string;
  }

  // Fallback: nested arrays
  for (const key of ["data", "outputs", "choices", "results"]) {
    const nested = d[key];
    if (Array.isArray(nested) && nested.length > 0) {
      const item = nested[0] as Record<string, unknown>;
      for (const k of ["text", "content", "output", "result", "message"]) {
        if (typeof item[k] === "string" && item[k]) return item[k] as string;
      }
      if (item.message && typeof item.message === "object") {
        const msg = item.message as Record<string, unknown>;
        if (typeof msg.content === "string" && msg.content) return msg.content;
      }
    }
  }

  return null;
}
\`\`\`
`;

function sessionLine(mode: SessionDocumentationMode): string {
  if (mode === 'required') {
    return 'Required — append &sessionId=<unique-slug> to the URL';
  }
  return 'Not Required';
}

function buildMarkdownTable(
  rows: BuildApiSchemaMarkdownParams['tableRows'],
): string {
  const header = '| # | inputId | Purpose |\n|---|---------|---------|';
  if (!rows.length) {
    return `${header}\n| — | — | — |`;
  }
  const body = rows
    .map(
      r =>
        `| ${r.index} | ${escapeTableCell(r.inputId)} | ${escapeTableCell(
          r.purpose,
        )} |`,
    )
    .join('\n');
  return `${header}\n${body}`;
}

export function buildApiSchemaMarkdown(
  params: BuildApiSchemaMarkdownParams,
): string {
  const baseUrlBlock = `## Base URL
\`\`\`
${params.apiBaseUrl}
\`\`\`
Append \`?flowId=<FLOW_ID>\` as a query parameter. For session-aware flows, also append \`&sessionId=<SESSION_ID>\`.
\`\`\`
${params.exampleUrlWithSession}
\`\`\`

### Calling a Specific Version
By default the API runs the live deployed version of the agent. To call a specific version instead, append \`&versionId=<VERSION_ID>\` to the URL. Version IDs are available once Version Control is enabled for the agent.
\`\`\`
${params.apiBaseUrl}?flowId=${params.flowId}&versionId=<VERSION_ID>
\`\`\`
 ---

`;

  const agentSection = `## Agent Specific Details: 
### Agent Name — ${params.agentName}

**Use for:** ${params.summary || '—'}

\`\`\`
Flow ID:   ${params.flowId}
Session:   ${sessionLine(params.sessionMode)}
Inputs:    ${params.inputCount}
\`\`\`

${buildMarkdownTable(params.tableRows)}

**Full example body:**

(Example of what you'd pass in)
\`\`\`json
${params.fullExampleBodyJson}
\`\`\`
`;

  return (
    STATIC_OVERVIEW_THROUGH_REQUEST +
    baseUrlBlock +
    STATIC_REQUEST_FORMAT +
    agentSection +
    STATIC_RESPONSE_PARSING
  );
}
